import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Menu, X, PanelLeftClose, PanelLeft, Sun, Moon, Monitor } from 'lucide-react'
import { MainNav, type NavGroup, type ContextualNav } from './MainNav'
import { UserMenu, type User } from './UserMenu'
import { BottomTabBar, type BottomTabRole } from './BottomTabBar'
import { SkipToContent } from './SkipToContent'
import { Breadcrumbs } from './Breadcrumbs'
import { NetworkStatusBanner } from './NetworkStatusBanner'
import type { ThemeMode } from '@/hooks/useDarkMode'
import { useCurrentEntity } from '@/contexts/EntityContext'
import { isNative } from '@/platform'

export interface AppShellProps {
  children: React.ReactNode
  navigationGroups: NavGroup[]
  contextualNav?: ContextualNav | null
  user?: User
  onNavigate?: (href: string) => void
  onCloseContextual?: () => void
  onLogout?: () => void
  onSettings?: () => void
  onHelp?: () => void
  onPermissions?: () => void
  onViewProfile?: () => void
  defaultCollapsed?: boolean
  themeMode?: ThemeMode
  onThemeToggle?: () => void
  /** Per-tab unread counts for the mobile bottom tab bar (e.g. associations, circles). */
  bottomTabBadges?: Record<string, number>
  /**
   * Surfaces the Relationships tab in the mobile bottom bar. Must stay in sync
   * with the Discover page, which only exposes the relationships sub-tab to
   * Circle Managers / Mafao employees.
   */
  isCircleManager?: boolean
}

export function AppShell({
  children,
  navigationGroups,
  contextualNav,
  user,
  onNavigate,
  onCloseContextual,
  onLogout,
  onSettings,
  onHelp,
  onPermissions,
  onViewProfile,
  defaultCollapsed = false,
  themeMode = 'auto',
  onThemeToggle,
  bottomTabBadges,
  isCircleManager,
}: AppShellProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const location = useLocation()
  const { currentEntity } = useCurrentEntity()
  // On Capacitor, each page owns its own top chrome (per the mobile design
  // mockups in product/sections/), so we drop the AppShell mobile header.
  // The web build keeps the hamburger header for smaller viewports.
  const native = isNative()

  const bottomTabRole: BottomTabRole =
    user?.accessRole === 'admin' ? 'admin'
    : user?.role === 'platform_admin' ? 'organizer'
    : 'member'

  const handleNavigate = (href: string) => {
    onNavigate?.(href)
    setIsMobileOpen(false)
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <SkipToContent />
      <NetworkStatusBanner />

      {/* Mobile header — web-only. Capacitor pages render their own hero. */}
      <header
        className={`${native ? 'hidden' : 'lg:hidden'} fixed top-0 left-0 right-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/60 dark:border-slate-800/60 flex items-center px-4 gap-3 h-14`}
        style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(3.5rem + env(safe-area-inset-top))' }}
      >
        <button
          onClick={() => setIsMobileOpen(true)}
          className="p-2 -ml-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all duration-150"
          aria-label="Open navigation"
          aria-expanded={isMobileOpen}
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-sm shadow-indigo-500/25">
            <span className="text-white font-bold text-xs tracking-tight">C</span>
          </div>
          <span className="font-semibold text-slate-900 dark:text-white text-sm tracking-tight">
            CircleUp
          </span>
        </div>
        {user && (
          <div className="ml-auto">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center ring-2 ring-white dark:ring-slate-900">
              <span className="text-[10px] font-bold text-white">
                {user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
              </span>
            </div>
          </div>
        )}
      </header>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full flex flex-col
          bg-slate-950 dark:bg-slate-950
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none
          ${isCollapsed ? 'w-[68px]' : 'w-[260px]'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div
          className={`h-16 flex items-center shrink-0 border-b border-white/[0.06] ${
            isCollapsed ? 'justify-center px-3' : 'px-5'
          }`}
        >
          {isCollapsed ? (
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <span className="text-white font-bold text-sm tracking-tight">C</span>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-1">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/20 shrink-0">
                <span className="text-white font-bold text-sm tracking-tight">C</span>
              </div>
              <div>
                <span className="font-semibold text-white text-[15px] tracking-tight">CircleUp</span>
                <span className="block text-[10px] text-slate-500 tracking-wide uppercase font-medium">Platform</span>
              </div>
            </div>
          )}

          {/* Mobile close */}
          <button
            onClick={() => setIsMobileOpen(false)}
            className="lg:hidden ml-auto p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-150"
            aria-label="Close navigation"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          <MainNav
            groups={navigationGroups}
            contextualNav={contextualNav}
            isCollapsed={isCollapsed}
            onNavigate={handleNavigate}
            onCloseContextual={onCloseContextual}
          />
        </div>

        {/* Bottom section: user + collapse */}
        <div className="shrink-0 border-t border-white/[0.06]">
          {user && (
            <UserMenu
              user={user}
              isCollapsed={isCollapsed}
              onLogout={onLogout}
              onSettings={onSettings}
              onHelp={onHelp}
              onPermissions={onPermissions}
              onViewProfile={onViewProfile}
            />
          )}

          {/* Theme toggle + Collapse toggle */}
          <div className={`flex ${isCollapsed ? 'flex-col' : ''} border-t border-white/[0.04]`}>
            <button
              onClick={onThemeToggle}
              className={`
                flex items-center gap-3 px-4 py-3 text-xs
                text-slate-500 hover:text-slate-300 hover:bg-white/[0.04] transition-all duration-150
                ${isCollapsed ? 'justify-center w-full' : 'flex-1'}
              `}
              aria-label={`Theme: ${themeMode} (click to cycle)`}
              title={`Theme: ${themeMode === 'auto' ? 'Auto (time-based)' : themeMode}`}
            >
              {themeMode === 'auto' ? (
                <>
                  <Monitor className="w-4 h-4" strokeWidth={1.5} />
                  {!isCollapsed && <span className="tracking-wide">Auto</span>}
                </>
              ) : themeMode === 'light' ? (
                <>
                  <Sun className="w-4 h-4" strokeWidth={1.5} />
                  {!isCollapsed && <span className="tracking-wide">Light</span>}
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4" strokeWidth={1.5} />
                  {!isCollapsed && <span className="tracking-wide">Dark</span>}
                </>
              )}
            </button>

            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`
                hidden lg:flex items-center gap-3 px-4 py-3 text-xs
                text-slate-500 hover:text-slate-300 hover:bg-white/[0.04] transition-all duration-150
                ${isCollapsed ? 'justify-center w-full' : 'flex-1'}
              `}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <PanelLeft className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <>
                  <PanelLeftClose className="w-4 h-4" strokeWidth={1.5} />
                  <span className="tracking-wide">Collapse</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        id="main-content"
        tabIndex={-1}
        className={`
          transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none min-h-screen
          ${native ? 'pt-0' : 'pt-[calc(3.5rem+env(safe-area-inset-top))]'} pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pt-0 lg:pb-0
          ${isCollapsed ? 'lg:pl-[68px]' : 'lg:pl-[260px]'}
        `}
      >
        {/* Breadcrumbs are web-only: on native each page owns its top chrome
            (header + back button), so a second breadcrumb row would sit under
            the status bar and overlap the page header. */}
        {!native && (
          <Breadcrumbs
            contextualNav={contextualNav ?? null}
            entityInfo={currentEntity}
            onNavigate={handleNavigate}
          />
        )}
        {children}
      </main>

      {/* Mobile bottom tab bar */}
      <BottomTabBar
        role={bottomTabRole}
        currentPath={location.pathname + location.search}
        onNavigate={handleNavigate}
        badges={bottomTabBadges}
        isCircleManager={isCircleManager}
      />
    </div>
  )
}
